import React, { useState } from 'react';
import '../styles/SelectedComps.css';
import { Arrow, ChevronWide, Copy, House, MapBubble, Sparkle, TickHeavy, Wand, Warning } from '../assets/icons';
import { colour, formatterCompact, formatterLong, numberFormatter } from '../styles/GlobalStyles';
import { Badge, Button, Dropdown, ExitScreen, IconButton, SmallCompCard, Tooltip } from './';
import { Download04Icon } from 'hugeicons-react';
import { getPriceField, recordEvent, setSelectedPropertyReportTransaction, setSelectedPropertyTransaction } from '../functions';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const getMinMaxPricePerSqFoot = (comps, selectedComps, selectedPanel) => {
    if (comps.length === 0) return { minPricePerSqFoot: null, maxPricePerSqFoot: null };

    const selectedPanelComps = selectedPanel === "all" 
        ? selectedComps 
        : selectedComps.filter(e => e.type === selectedPanel);

    let minPricePerSqFoot = Infinity;
    let maxPricePerSqFoot = -Infinity;

    comps.forEach(comp => {
        const compIndex = selectedPanelComps.findIndex(e => e.zpid === comp.uniqueIds.zillowId);
        
        // Skip this iteration if compIndex is -1 (not found)
        if (compIndex === -1) return;
        const squareFootage = comp.propertyAttributes.livingSquareFootage;
        const price = getPriceField(comp);
        const pricePerSqFoot = price / squareFootage;

        // Exclude null values
        if (pricePerSqFoot === null) return;

        if (pricePerSqFoot < minPricePerSqFoot) minPricePerSqFoot = pricePerSqFoot;
        if (pricePerSqFoot > maxPricePerSqFoot) maxPricePerSqFoot = pricePerSqFoot;
    });

    return { 
        minPricePerSqFoot: minPricePerSqFoot === Infinity ? null : minPricePerSqFoot, 
        maxPricePerSqFoot: maxPricePerSqFoot === -Infinity ? null : maxPricePerSqFoot 
    };
};

const getMinMaxNovationDOM = (comps, selectedComps, selectedPanel) => {
    if (comps.length === 0) return { minDOM: null, maxDOM: null };

    const selectedPanelComps = selectedPanel === "all" 
        ? selectedComps 
        : selectedComps.filter(e => e.type === selectedPanel);

    let minDOM = Infinity;
    let maxDOM = -Infinity;

    comps.forEach(comp => {
        const compIndex = selectedPanelComps.findIndex(e => e.zpid === comp.uniqueIds.zillowId);
        
        // Skip this iteration if compIndex is -1 (not found)
        if (compIndex === -1) return;

        const novationDOM = comp.novationDOM;

        // Exclude null values
        if (novationDOM === null) return;

        if (novationDOM < minDOM) minDOM = novationDOM;
        if (novationDOM > maxDOM) maxDOM = novationDOM;
    });

    return { 
        minDOM: minDOM === Infinity ? null : minDOM, 
        maxDOM: maxDOM === -Infinity ? null : maxDOM 
    };
};

function SelectedComps(props) {
    const allSelectedComps = props.allSelectedComps;
    const setAllSelectedComps = props.setAllSelectedComps;
    const fullSelectedComps = props.fullSelectedComps;
    const toggleSelectedComp = props.toggleSelectedComp;
    const expanded = props.expanded;
    const setExpanded = props.setExpanded;
    const showScrollableImages = props.showScrollableImages;
    const openDetailedView = props.openDetailedView;
    const downloadProperties = props.downloadProperties;
    const shareLink = props.shareLink;
    const downloadLoading = props.downloadLoading;
    const selectedPanel = props.selectedPanel;
    const setSelectedPanel = props.setSelectedPanel;
    const compTypeDropdownOpen = props.compTypeDropdownOpen;
    const setCompTypeDropdownOpen = props.setCompTypeDropdownOpen;
    const closeDropdowns = props.closeDropdowns;
    const companyId = props.companyId;
    const property = props.property;
    const linkCopied = props.linkCopied;
    const linkCopiedError = props.linkCopiedError;
    const readOnly = props.readOnly;
    const compTypeLegend = props.compTypeLegend;
    const isLoaded = props.isLoaded;
    const bestComps = props.bestComps;
    const innerWidth = props.innerWidth;
    const getARV = props.getARV;
    const arvLoading = props.arvLoading;
    const arvEstimate = props.arvEstimate;
    const getAsIs = props.getAsIs;
    const asIsEstimate = props.asIsEstimate;
    const asIsDescription = props.asIsDescription;
    const mobileSelectedCompsBody = props.mobileSelectedCompsBody;
    const autoKeepComps = props.autoKeepComps;
    const compsChanged = props.compsChanged;
    const setRegenerateARVErrorModal = props.setRegenerateARVErrorModal;
    const actionRequired = props.actionRequired;
    const setSmallChatExpanded = props.setSmallChatExpanded;
    const bodyRef = props.bodyRef;
    const bodyWidth = bodyRef.current?.offsetWidth;
    const arvComps = allSelectedComps.filter(e => e.type === "arv");
    // const novationComps = allSelectedComps.filter(e => e.type === "novation");
    const asIsComps = allSelectedComps.filter(e => e.type === "as-is");
    const { minPricePerSqFoot, maxPricePerSqFoot } = getMinMaxPricePerSqFoot(fullSelectedComps, allSelectedComps, selectedPanel);
    const { minDOM, maxDOM } = getMinMaxNovationDOM(fullSelectedComps, allSelectedComps, selectedPanel);
    const dropdownOpenObject = compTypeDropdownOpen === null ? [] : allSelectedComps.filter(e => e.zpid === compTypeDropdownOpen)[0];
    const [arvEstimateHover, setArvEstimateHover] = useState(false);
    const [downloadTooltip, setDownloadTooltip] = useState(false);

    const toggleExpanded = () => {
        setExpanded(!expanded);

        if ( !expanded === false ) {
            setCompTypeDropdownOpen(null);
            recordEvent("close_wizard", {});
        }
        else {
            recordEvent("open_wizard", {});
            if ( mobileSelectedCompsBody.current !== null ) {
                setTimeout(() => {
                    mobileSelectedCompsBody.current.scrollTop = 0;
                }, 50);
            }
        }
    };

    const removeComp = (item) => {
        toggleSelectedComp(item);
    };

    const options = [
        {
            label: `All (${allSelectedComps.length})`,
            value: "all",
            selected: selectedPanel === "all" ? true : false,
            rangeTitle: "Range"
        },
        {
            label: `ARV (${arvComps.length})`,
            value: "arv",
            selected: selectedPanel === "arv" ? true : false,
            rangeTitle: "Range"
        },
        {
            label: `As-is (${asIsComps.length})`,
            value: "as-is",
            selected: selectedPanel === "as-is" ? true : false,
            rangeTitle: "Range"
        },
        // {
        //     label: `Novation (${novationComps.length})`,
        //     value: "novation",
        //     selected: selectedPanel === "novation" ? true : false,
        //     rangeTitle: "Range"
        // }
    ];

    const onChangeSelected = (value) => {
        setSelectedPanel(value);
    };

    const toggleCompTypeDropdown = (zpid) => {
        setCompTypeDropdownOpen(compTypeDropdownOpen === zpid ? null : zpid);
    };

    const changeCompStatus = (item, index) => {
        const value = item.value;
        const zpid = dropdownOpenObject.zpid;

        const cloneAllComps = [...allSelectedComps];
        const compIndex = cloneAllComps.findIndex(e => e.zpid === zpid);
        cloneAllComps[compIndex].type = value;
        setAllSelectedComps(cloneAllComps);
        setCompTypeDropdownOpen(null);

        if ( companyId !== null ) {
            const colRef = "companies";
            const docRef = companyId;
            const subColRef = "properties";
            const subDocRef = property.uniqueIds.propertyId;
            setSelectedPropertyTransaction(colRef, docRef, subColRef, subDocRef, cloneAllComps);
            setSelectedPropertyReportTransaction("reports", docRef, subColRef, subDocRef, cloneAllComps);
        }

        const remainingCompsInSection = cloneAllComps.filter(e => e.type === selectedPanel).length;
        if ( remainingCompsInSection === 0 ) {
            setSelectedPanel("all");
        }
        recordEvent("change_comp_status", {
            zpid: zpid,
            value: value
        });
    };

    const compTypeOptions = [
        {
            label: "ARV",
            value: "arv",
            selected: dropdownOpenObject.type === "arv" ? true : false
        },
        {
            label: "As-is",
            value: "as-is",
            selected: dropdownOpenObject.type === "as-is" ? true : false
        },
        // {
        //     label: "Novation",
        //     value: "novation",
        //     selected: dropdownOpenObject.type === "novation" ? true : false
        // }
    ];

    const getLabel = (item) => {
        const getType = allSelectedComps.filter(e => e.zpid === item.uniqueIds.zillowId)[0].type;
        if ( getType === null ) {
            return "Unspecified";
        }
        else {
            const getTypeObject = compTypeOptions.filter(e => e.value === getType)[0];
            return getTypeObject.label;
        }
    };

    const checkZPIDCategory = (zpid) => {
        const compIndex = allSelectedComps.findIndex(e => e.zpid === zpid);
        const compType = allSelectedComps[compIndex].type;
        if (selectedPanel === "all") {
            return true;
        }
        else {
            return compType === selectedPanel;
        }
    };

    const getCompTypeObject = (item) => {
        const zpid = item.uniqueIds.zillowId;
        const compIndex = allSelectedComps.findIndex(e => e.zpid === zpid);
        if ( compIndex === -1 ) {
            return null;
        }
        const compType = allSelectedComps[compIndex].type;
        const compTypeObject = compTypeLegend.filter(e => e.value === compType)[0];
        return compTypeObject;
    }

    const openARVErrorModal = () => {
        if ( setRegenerateARVErrorModal ) {
            recordEvent("open_arv_error_modal", {});
            setRegenerateARVErrorModal(true);
        }
    };

    return (
        <div 
            className="selected-comps-outer-container bg-colour-white"
            style={{
                "--body-width": bodyWidth ? `${bodyWidth}px` : "auto"
            }}
        >
            {
                compTypeDropdownOpen !== null &&
                <ExitScreen
                    clickScreen={() => closeDropdowns()}
                />
            }
            <div className="selected-comps-inner-container mobile-none">
                <div className={"selected-comps-top-container " + (expanded === true ? "selected-comps-expanded-inner-container" : "selected-comps-unexpanded-inner-container")}>
                    <div className="selected-comps-top-inner-container">
                        <div className="selected-comps-top-left-container">
                            <div className="selected-comps-top-left-title-container">
                                {/* <span className="label-regular colour-secondary">
                                    Kept comps ({allSelectedComps.length})
                                </span> */}
                                <div className="selected-comps-price-timeline-row">
                                    {
                                        actionRequired === true && innerWidth > 1000 && readOnly === false ?
                                        <div className="selected-comps-arv-estimate-outer-container relative-container">
                                            <div className="selected-comps-arv-estimate-container">
                                                <Sparkle
                                                    className=""
                                                    fill={colour.gray06}
                                                />
                                                <div className="selected-comps-arv-estimate-loading-container relative-container">
                                                    <span className="heading-small-bold colour-primary">
                                                        ARV
                                                    </span>
                                                </div>
                                                <div
                                                    className="cursor-pointer"
                                                    onClick={() => setSmallChatExpanded === null ? null : setSmallChatExpanded(true)}
                                                >
                                                    <Badge
                                                        bgColour={"bg-colour-red-01"}
                                                        text="Action required"
                                                        statusDot={false}
                                                        statusDotColour={"colour-error"}
                                                        textClass="body-regular colour-primary"
                                                        icon={<Warning className="" fill={colour.red02} />}
                                                        tooltip={false}
                                                        tooltipBody={null}
                                                        tooltipPosition={null}
                                                        iconLeft={true}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        :
                                        arvLoading === true && innerWidth > 1000 && readOnly === false ?
                                        <div className="selected-comps-arv-estimate-outer-container relative-container">
                                            <div className="selected-comps-arv-estimate-container">
                                                {/* <Sparkle
                                                    className=""
                                                    fill={colour.gray06}
                                                /> */}
                                                <div className="selected-comps-arv-estimate-loading-container relative-container">
                                                    <span className="heading-small-bold colour-primary">
                                                        Generating {selectedPanel === "as-is" ? "As-is" : "ARV"}..
                                                    </span>
                                                    <DotLottieReact
                                                        src="https://lottie.host/34c9a7db-dd11-4c1a-9887-fa6d71d08da0/EQsHF5pebc.lottie"
                                                        loop
                                                        autoplay
                                                        className="selected-comps-arv-estimate-loading"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        :
                                        arvEstimate !== null && innerWidth >= 1000 && (selectedPanel === "arv" || selectedPanel === "all") ?
                                        <div className="selected-comps-arv-estimate-outer-container relative-container">
                                            <div className="selected-comps-arv-estimate-container">
                                                <Sparkle
                                                    className=""
                                                    fill={colour.gray06}
                                                />
                                                <span className="heading-small-bold colour-primary">
                                                    {
                                                        (innerWidth < 1280 && innerWidth >= 1000 && readOnly === true) || (innerWidth < 1640 && innerWidth >= 1000 && readOnly === false) ?
                                                        "ARV"
                                                        :
                                                        "ARV Estimate"
                                                    }
                                                </span>
                                                <span className="heading-small-regular colour-primary">
                                                    {formatterLong.format(arvEstimate).replace(".00", "")}
                                                </span>
                                            </div>
                                        </div>
                                        :
                                        asIsEstimate !== null && innerWidth > 1000 && selectedPanel === "as-is" ?
                                        <div 
                                            className="selected-comps-arv-estimate-outer-container relative-container"
                                            onMouseEnter={() => setArvEstimateHover(true)}
                                            onMouseLeave={() => setArvEstimateHover(false)}
                                        >
                                            <div className="selected-comps-arv-estimate-container">
                                                <Sparkle
                                                    className=""
                                                    fill={colour.gray06}
                                                />
                                                <span className="heading-small-bold colour-primary">
                                                    As-is Estimate
                                                </span>
                                                <span className="heading-small-regular colour-primary">
                                                    {formatterLong.format(asIsEstimate).replace(".00", "")}
                                                </span>
                                            </div>
                                            {
                                                arvEstimateHover === true &&
                                                <Tooltip
                                                    type="info"
                                                    title={"As-is Estimate"}
                                                    body={asIsDescription}
                                                    position={expanded === true ? "top" : "bottom"}
                                                    contextTooltipBg={null}
                                                    exit={false}
                                                    exitFunc={null}
                                                    customClass={expanded === true ? "tooltip-arv-expanded" : "tooltip-arv-unexpanded"}
                                                />
                                            }
                                        </div>
                                        :
                                        null
                                    }
                                    {
                                        minPricePerSqFoot === null && maxPricePerSqFoot === null ?
                                        null
                                        :
                                        <div className="selected-comps-price-range-container">
                                            <span className={(innerWidth < 700 ? "body-bold colour-primary" : "heading-small-bold colour-primary")}>
                                                Price/sqft
                                            </span>
                                            <span className={(innerWidth < 700 ? "body-regular colour-primary" : "heading-small-regular colour-primary")}>
                                                {
                                                    minPricePerSqFoot === maxPricePerSqFoot ?
                                                    formatterCompact.format(minPricePerSqFoot).replace(".00", "")
                                                    :
                                                    `${formatterCompact.format(minPricePerSqFoot).replace(".00", "")} - ${formatterCompact.format(maxPricePerSqFoot).replace(".00", "")}`
                                                }
                                            </span>
                                        </div>
                                    }
                                    {
                                        minDOM === null && maxDOM === null ?
                                        null
                                        :
                                        innerWidth < 450 ?
                                        null
                                        :
                                        readOnly === false && innerWidth > 1000 && innerWidth < 1500 ?
                                        null
                                        :
                                        <div className="selected-comps-price-range-container">
                                            <span className={(innerWidth < 700 ? "body-bold colour-primary" : "heading-small-bold colour-primary")}>
                                                {
                                                    (innerWidth < 1280 && innerWidth >= 1000 && readOnly === true) || (innerWidth < 1640 && innerWidth >= 1000 && readOnly === false) ?
                                                    "DOM"
                                                    :
                                                    "Days on market"
                                                }
                                            </span>
                                            <span className={(innerWidth < 700 ? "body-regular colour-primary" : "heading-small-regular colour-primary")}>
                                                {
                                                    minDOM === maxDOM ?
                                                    numberFormatter.format(minDOM)
                                                    :
                                                    `${numberFormatter.format(minDOM)} - ${numberFormatter.format(maxDOM)}`
                                                }
                                            </span>
                                        </div>
                                    }
                                </div>
                                <div className="desktop-none">
                                    <IconButton
                                        size="large"
                                        icon={<Arrow fill={colour.gray06} className="selected-comps-mobile-arrow reverse" />}
                                        disabled={false}
                                        clickFunc={() => toggleExpanded()}
                                        standardIcon={false}
                                        background={true}
                                        bgType={"white"}
                                        contextTooltip={false}
                                        contextTooltipBG={null}
                                        contextTooltipText={null}
                                        customClass={null}
                                    />
                                </div>
                            </div>
                            {
                                arvLoading === false &&
                                <div className={"selected-comps-count-container " + (expanded === false ? "view-none" : "")}>
                                    {
                                        options.map((item, index) =>
                                            <div 
                                                className="selected-comps-count-element"
                                                key={index}
                                                onClick={() => item.label.includes("(0)") ? null : onChangeSelected(item.value)}
                                            >
                                                <span className={(innerWidth < 400 ? "label-regular " : "body-regular ") + (item.selected === true ? "colour-primary underline " : "colour-secondary ") + (item.label.includes("(0)") ? "" : "cursor-pointer")}>
                                                    {item.label}
                                                </span>
                                            </div>
                                        )
                                    }
                                </div>
                            }
                        </div>
                        <div className="selected-comps-top-right-container">
                            {
                                getARV !== null && readOnly === false && (selectedPanel === "all" || selectedPanel === "arv") ?
                                <div className={"selected-comps-get-arv-container " + (arvLoading === true ? "selected-comps-get-arv-container-loading" : "")}>
                                    <Button
                                        buttonType="primary-button"
                                        size="large"
                                        text={"Regenerate ARV"}
                                        cta={() => getARV()}
                                        disabled={arvComps.length < 2 ? true : actionRequired === true ? true : arvEstimate === null ? false : !compsChanged}
                                        leftIcon={arvLoading === true ? null : <Wand fill={colour.gray00} className="selected-comps-wand" />}
                                        leftIconClass=""
                                        rightIcon={null}
                                        rightIconClass=""
                                        alt=""
                                        standardIcon={false}
                                        badge={false}
                                        badgeVal={null}
                                        badgeBG={null}
                                        buttonRef={null}
                                        spinner={arvLoading}
                                        disabledAction={openARVErrorModal}
                                    />
                                </div>
                                :
                                getAsIs !== null && asIsComps.length >= 2 && readOnly === false && selectedPanel === "as-is" ?
                                <div className={"selected-comps-get-arv-container " + (arvLoading === true ? "selected-comps-get-arv-container-loading" : "")}>
                                    <Button
                                        buttonType="primary-button"
                                        size="large"
                                        text={"Get As-is Estimate"}
                                        cta={() => getAsIs()}
                                        disabled={false}
                                        leftIcon={arvLoading === true ? null : <Wand fill={colour.gray00} className="selected-comps-wand" />}
                                        leftIconClass=""
                                        rightIcon={null}
                                        rightIconClass=""
                                        alt=""
                                        standardIcon={false}
                                        badge={false}
                                        badgeVal={null}
                                        badgeBG={null}
                                        buttonRef={null}
                                        spinner={arvLoading}
                                    />
                                </div>
                                :
                                null
                            }
                            {
                                allSelectedComps.length > 0 && companyId !== null && readOnly === false &&
                                <div className="selected-comps-share-container">
                                    <Button
                                        buttonType="secondary-button"
                                        size="large"
                                        text={linkCopied === true ? "Link copied" : "Share link"}
                                        cta={() => shareLink()}
                                        disabled={false}
                                        leftIcon={linkCopied === true ? <TickHeavy fill={colour.gray06} className="" /> : <Copy fill={colour.gray06} className="" />}
                                        leftIconClass=""
                                        rightIcon={null}
                                        rightIconClass=""
                                        alt=""
                                        standardIcon={false}
                                        badge={false}
                                        badgeVal={null}
                                        badgeBG={null}
                                        buttonRef={null}
                                        spinner={false}
                                    />
                                    {
                                        linkCopiedError === true &&
                                        <span className="body-regular colour-error">
                                            There was an error copying the link
                                        </span>
                                    }
                                </div>
                            }
                            {
                                allSelectedComps.length > 0 &&
                                <div 
                                    className="selected-comps-download-container relative-container"
                                    onMouseEnter={() => setDownloadTooltip(true)}
                                    onMouseLeave={() => setDownloadTooltip(false)}
                                >
                                    <IconButton
                                        size="medium"
                                        icon={<Download04Icon color={colour.gray06} size={16} />}
                                        disabled={downloadLoading}
                                        clickFunc={() => downloadProperties()}
                                        standardIcon={false}
                                        background={true}
                                        bgType={"white"}
                                        contextTooltip={downloadTooltip}
                                        contextTooltipBG={"dark"}
                                        contextTooltipText={"Download CSV"}
                                        customClass={null}
                                    />
                                </div>
                            }
                            <div 
                                className="selected-comps-expand-container text-button"
                                onClick={() => toggleExpanded()}
                            >
                                <span className="body-regular colour-primary">
                                    {
                                        expanded ? "Collapse" : "Expand"
                                    }
                                </span>
                                <ChevronWide
                                    fill={colour.gray07}
                                    className={expanded === false ? "reverse" : "" }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"selected-comps-bottom-container " + (expanded === true ? "selected-comps-bottom-container-expanded" : "selected-comps-bottom-container-unexpanded")}>
                    {
                        fullSelectedComps.length > 0 &&
                        <div className="selected-comps-card-container">
                            {
                                fullSelectedComps.map((item, index) =>
                                    (selectedPanel === "all" || checkZPIDCategory(item.uniqueIds.zillowId) === true) &&
                                    <div 
                                        className="selected-comps-card-element"
                                        key={index}
                                    >
                                        <SmallCompCard
                                            property={item}
                                            closeCompCard={null}
                                            selected={true}
                                            addSelectedComp={readOnly === true ? null : removeComp}
                                            overlay={true}
                                            showScrollableImages={showScrollableImages}
                                            slideshow={false}
                                            openDetailedView={openDetailedView}
                                            expanded={expanded}
                                            subjectPropertyObject={property}
                                            isLoaded={isLoaded}
                                            bestComp={bestComps.findIndex(e => e.uniqueIds.zillowId === item.uniqueIds.zillowId) !== -1 ? true : false}
                                        />
                                        {
                                            readOnly === false ?
                                            <Dropdown
                                                open={compTypeDropdownOpen === item.uniqueIds.zillowId ? true : false}
                                                icon={null}
                                                label={getLabel(item)}
                                                disabled={false}
                                                options={compTypeOptions}
                                                multiselect={false}
                                                toggleDropdown={() => expanded === false ? null : toggleCompTypeDropdown(item.uniqueIds.zillowId)}
                                                cta={(item, index) => expanded === false ? null : changeCompStatus(item, index)}
                                                sidealign={"left"}
                                                topalign={"top"}
                                                indexSelect={false}
                                                selectedIndex={null}
                                            />
                                            :
                                            getCompTypeObject(item) !== null ?
                                            <div className="run-comps-best-comps-title-container">
                                                <div className="relative-container">
                                                    <MapBubble
                                                        className=""
                                                        fill={getCompTypeObject(item).fill}
                                                        stroke={getCompTypeObject(item).stroke}
                                                        strokeWidth="0.5"
                                                    />
                                                    <House
                                                        fill={getCompTypeObject(item).house}
                                                        className="all-comps-map-house-icon"
                                                    />
                                                </div>
                                                <span className="body-bold colour-primary">
                                                    {getCompTypeObject(item).label.slice(0, -1)}
                                                </span>
                                            </div>
                                            :
                                            null
                                        }
                                    </div>
                                )
                            }
                        </div>
                    }
                </div>
            </div>
            <div className="selected-comps-inner-container desktop-none">
                <div className="selected-comps-mobile-left-container">
                    <div className="selected-comps-mobile-arv-estimate-container">
                        <Sparkle
                            className=""
                            fill={colour.gray06}
                        />
                        <span className="body-bold colour-primary">
                            ARV Est
                        </span>
                    </div>
                    <span className="body-regular colour-primary">
                        {
                            arvLoading === true ?
                            "Thinkin'.."
                            :
                            arvEstimate !== null ?
                            formatterLong.format(arvEstimate).replace(".00", "")
                            :
                            "Auto-save to get ARV"
                        }
                    </span>
                </div>
                {
                    readOnly === false &&
                    <div className="selected-comps-mobile-right-container">
                        {
                            getARV !== null && readOnly === false ?
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text={"Regenerate ARV"}
                                cta={() => getARV()}
                                disabled={arvComps.length < 2 ? true : actionRequired === true ? true : arvEstimate === null ? false : !compsChanged}
                                leftIcon={arvLoading === true ? null : <Wand fill={colour.gray00} className="selected-comps-wand" />}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={arvLoading}
                                disabledAction={openARVErrorModal}
                            />
                            :
                            getARV !== null && arvComps.length < 2 && readOnly === false && autoKeepComps !== null ?
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text={"Auto-keep comps"}
                                cta={() => autoKeepComps()}
                                disabled={false}
                                leftIcon={arvLoading === true ? null : <Wand fill={colour.gray00} className="selected-comps-wand" />}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={arvLoading}
                            />
                            :
                            null
                        }
                    </div>
                }
            </div>
        </div>
    )
};

export default SelectedComps;